/**
 * Routing and the authentication gate.
 *
 * Which portal a user gets is decided by the hostname (education.* versus
 * myeducation.*) rather than by their role, with the server enforcing that a
 * staff account cannot sign in at the family portal and vice versa. Doing it
 * by hostname is what lets the two be split onto separate deployments later
 * without touching a component.
 *
 * Printable documents render **outside** the shell. A report card with a
 * navigation bar above it is a screenshot, not a document, and the brief is
 * explicit that reports must use dedicated print layouts rather than printing
 * the application page.
 *
 * Routes that are not built yet resolve to a page that says so and offers a
 * way back. A menu item leading nowhere is worse than no menu item.
 */

import { Suspense } from 'react'
import { lazyRoute } from './lazyRoute'
import { Navigate, Link, Route, Routes, useLocation } from 'react-router-dom'
import { currentPortal } from '@/lib/api'
import { useSession } from '@/lib/session'
import { ChangePasswordPage } from '@/features/auth/ChangePasswordPage'
import { MfaEnrollPage } from '@/features/auth/MfaEnrollPage'
import { AcceptInvitationPage } from '@/features/auth/AcceptInvitationPage'
import { JoinPage } from '@/features/focusGroup/JoinPage'
import { AppShell } from '@/components/AppShell'
import { Card, ErrorState, Skeleton } from '@/components/ui'
import { LoginPage } from '@/features/auth/LoginPage'

const FeedbackLabPage = lazyRoute(() =>
  import('@/features/feedback/FeedbackLabPage').then((m) => ({ default: m.FeedbackLabPage })),
)
const FeedbackInboxPage = lazyRoute(() =>
  import('@/features/feedback/FeedbackInboxPage').then((m) => ({ default: m.FeedbackInboxPage })),
)
const FocusGroupPage = lazyRoute(() =>
  import('@/features/focusGroup/FocusGroupPage').then((m) => ({ default: m.FocusGroupPage })),
)
const TesterDetailPage = lazyRoute(() =>
  import('@/features/focusGroup/TesterDetailPage').then((m) => ({ default: m.TesterDetailPage })),
)
const PublicCalendarPage = lazyRoute(() => import('@/features/calendar/CalendarExtras').then((m) => ({ default: m.PublicCalendarPage })))
const CalendarHome = lazyRoute(() => import('@/features/calendar/CalendarHome').then((m) => ({ default: m.CalendarHome })))
const MyWorkPage = lazyRoute(() => import('@/features/work/MyWorkPage').then((m) => ({ default: m.MyWorkPage })))
const SpecialProgramsPage = lazyRoute(() =>
  import('@/features/specialPrograms/SpecialProgramsPage').then((m) => ({ default: m.SpecialProgramsPage })),
)
const CollectPage = lazyRoute(() =>
  import('@/features/specialPrograms/CollectPage').then((m) => ({ default: m.CollectPage })),
)
const GrowthPage = lazyRoute(() => import('@/features/growth/GrowthPage').then((m) => ({ default: m.GrowthPage })))
const AdmissionsPage = lazyRoute(() =>
  import('@/features/admissions/AdmissionsPage').then((m) => ({ default: m.AdmissionsPage })),
)
const ApplyPage = lazyRoute(() => import('@/portals/family/ApplyPage').then((m) => ({ default: m.ApplyPage })))
const FormsPage = lazyRoute(() => import('@/features/forms/FormsPage').then((m) => ({ default: m.FormsPage })))
const FamilyFormsPage = lazyRoute(() =>
  import('@/portals/family/FamilyFormsPage').then((m) => ({ default: m.FamilyFormsPage })),
)
const FeesHome = lazyRoute(() => import('@/features/fees/FeesHome').then((m) => ({ default: m.FeesHome })))
const FeeStudentPage = lazyRoute(() => import('@/features/fees/FeesHome').then((m) => ({ default: m.FeeStudentPage })))
const StudentPage = lazyRoute(() => import('@/portals/family/StudentPage').then((m) => ({ default: m.StudentPage })))
const FamilyFeesPage = lazyRoute(() =>
  import('@/portals/family/FamilyFeesPage').then((m) => ({ default: m.FamilyFeesPage })),
)
const SchedulingPage = lazyRoute(() =>
  import('@/features/scheduling/SchedulingPage').then((m) => ({ default: m.SchedulingPage })),
)
const CourseCatalogPage = lazyRoute(() =>
  import('@/features/courses/CourseCatalogPage').then((m) => ({ default: m.CourseCatalogPage })),
)
const ScenarioRunsPage = lazyRoute(() =>
  import('@/features/scheduling/ScenarioRunsPage').then((m) => ({ default: m.ScenarioRunsPage })),
)
const CourseRequestsPage = lazyRoute(() =>
  import('@/portals/family/CourseRequestsPage').then((m) => ({ default: m.CourseRequestsPage })),
)
const GraduationPage = lazyRoute(() =>
  import('@/features/graduation/GraduationPage').then((m) => ({ default: m.GraduationPage })),
)
const FamilyGraduationPage = lazyRoute(() =>
  import('@/portals/family/FamilyGraduationPage').then((m) => ({ default: m.FamilyGraduationPage })),
)
const CounselorPage = lazyRoute(() =>
  import('@/features/counseling/CounselorPage').then((m) => ({ default: m.CounselorPage })),
)
const MyClassPage = lazyRoute(() => import('@/features/myclass/MyClassPage'))
const IntegrityCheckPage = lazyRoute(() => import('@/features/myclass/IntegrityCheckPage'))
const CollegeCareerPage = lazyRoute(() =>
  import('@/portals/family/CollegeCareerPage').then((m) => ({ default: m.CollegeCareerPage })),
)
const FamilyHealthPage = lazyRoute(() =>
  import('@/portals/family/FamilyHealthPage').then((m) => ({ default: m.FamilyHealthPage })),
)
const FamilyActivitiesPage = lazyRoute(() =>
  import('@/portals/family/FamilyActivitiesPage').then((m) => ({ default: m.FamilyActivitiesPage })),
)
const FamilyDismissalPage = lazyRoute(() => import('@/portals/family/FamilyDismissalPage').then((m) => ({ default: m.FamilyDismissalPage })))
const FamilyLibraryPage = lazyRoute(() => import('@/portals/family/FamilyLibraryPage').then((m) => ({ default: m.FamilyLibraryPage })))
const FamilyCafeteriaPage = lazyRoute(() =>
  import('@/portals/family/FamilyCafeteriaPage').then((m) => ({ default: m.FamilyCafeteriaPage })),
)
const AssessmentsHome = lazyRoute(() => import('@/features/assessments/AssessmentsHome').then((m) => ({ default: m.AssessmentsHome })))
const AssessmentDetail = lazyRoute(() => import('@/features/assessments/AssessmentDetail').then((m) => ({ default: m.AssessmentDetail })))
const AssessmentSessionPage = lazyRoute(() => import('@/features/assessments/SessionPage').then((m) => ({ default: m.SessionPage })))
const CurriculumHome = lazyRoute(() => import('@/features/curriculum/CurriculumHome').then((m) => ({ default: m.CurriculumHome })))
const CurriculumMapDetail = lazyRoute(() => import('@/features/curriculum/MapDetail').then((m) => ({ default: m.MapDetail })))
const TransportationPage = lazyRoute(() =>
  import('@/features/transportation/TransportationHome').then((m) => ({ default: m.TransportationRoutes })),
)
const DismissalPage = lazyRoute(() =>
  import('@/features/dismissal/DismissalPage').then((m) => ({ default: m.DismissalPage })),
)
const HealthOfficePage = lazyRoute(() =>
  import('@/features/health/HealthOfficePage').then((m) => ({ default: m.HealthOfficePage })),
)
/** The same office on a phone: the nurse's rounds, away from the desk. */
const NurseRoundsPage = lazyRoute(() =>
  import('@/features/edops/NurseRoundsPage').then((m) => ({ default: m.NurseRoundsPage })),
)
const DisciplinePage = lazyRoute(() =>
  import('@/features/discipline/DisciplinePage').then((m) => ({ default: m.DisciplinePage })),
)
const ActivitiesHome = lazyRoute(() =>
  import('@/features/activities/ActivitiesHome').then((m) => ({ default: m.ActivitiesHome })),
)
const ActivityWorkspace = lazyRoute(() =>
  import('@/features/activities/ActivityWorkspace').then((m) => ({ default: m.ActivityWorkspace })),
)
const CafeteriaPage = lazyRoute(() =>
  import('@/features/cafeteria/CafeteriaPage').then((m) => ({ default: m.CafeteriaPage })),
)
const PosPage = lazyRoute(() =>
  import('@/features/cafeteria/PosPage').then((m) => ({ default: m.PosPage })),
)
const AssetsRoutes = lazyRoute(() =>
  import('@/features/assets/AssetsHome').then((m) => ({ default: m.AssetsRoutes })),
)
const StaffHome = lazyRoute(() => import('@/features/staff/StaffHome').then((m) => ({ default: m.StaffHome })))
const StaffRecordPage = lazyRoute(() => import('@/features/staff/StaffRecord').then((m) => ({ default: m.StaffRecordPage })))
const AbsencesHome = lazyRoute(() => import('@/features/absences/AbsencesHome').then((m) => ({ default: m.AbsencesHome })))
const AbsenceDetailPage = lazyRoute(() => import('@/features/absences/AbsenceDetail').then((m) => ({ default: m.AbsenceDetailPage })))

const SubAssignmentPage = lazyRoute(() => import('@/features/absences/SubAssignmentPage').then((m) => ({ default: m.SubAssignmentPage })))
const EvaluationsHome = lazyRoute(() => import('@/features/evaluations/EvaluationsHome').then((m) => ({ default: m.EvaluationsHome })))
const WalkthroughPage = lazyRoute(() => import('@/features/evaluations/WalkthroughPage').then((m) => ({ default: m.WalkthroughPage })))
const CycleDetailPage = lazyRoute(() => import('@/features/evaluations/CycleDetail').then((m) => ({ default: m.CycleDetailPage })))
const EmergencyPage = lazyRoute(() =>
  import('@/features/emergency/EmergencyPage').then((m) => ({ default: m.EmergencyPage })),
)
const AdministrationPage = lazyRoute(() =>
  import('@/features/admin/AdministrationPage').then((m) => ({ default: m.AdministrationPage })),
)
const VisitorsPage = lazyRoute(() =>
  import('@/features/visitors/VisitorsPage').then((m) => ({ default: m.VisitorsPage })),
)
const LibraryPage = lazyRoute(() =>
  import('@/features/library/LibraryPage').then((m) => ({ default: m.LibraryPage })),
)
const FacilitiesPage = lazyRoute(() =>
  import('@/features/facilities/FacilitiesHome').then((m) => ({ default: m.FacilitiesRoutes })),
)
const PurchasingHome = lazyRoute(() => import('@/features/purchasing/PurchasingHome').then((m) => ({ default: m.PurchasingHome })))
const PurchaseOrderPage = lazyRoute(() => import('@/features/purchasing/OrderDetail').then((m) => ({ default: m.OrderDetailPage })))
const VendorPoPage = lazyRoute(() => import('@/features/purchasing/VendorPoPage').then((m) => ({ default: m.VendorPoPage })))
const PurchaseRequestPage = lazyRoute(() => import('@/features/purchasing/RequestDetail').then((m) => ({ default: m.RequestDetailPage })))
const FinancePage = lazyRoute(() =>
  import('@/features/finance/FinancePage').then((m) => ({ default: m.FinancePage })),
)
const FamilyMessagesPage = lazyRoute(() =>
  import('@/features/messages/MessagesHome').then((m) => ({ default: m.FamilyMessages })),
)
const MessagesHome = lazyRoute(() =>
  import('@/features/messages/MessagesHome').then((m) => ({ default: m.MessagesHome })),
)
const FamilySchedulePage = lazyRoute(() =>
  import('@/portals/family/FamilyMorePages').then((m) => ({ default: m.FamilySchedulePage })),
)
const FamilyCalendarPage = lazyRoute(() =>
  import('@/portals/family/FamilyMorePages').then((m) => ({ default: m.FamilyCalendarPage })),
)
const FamilyDocumentsPage = lazyRoute(() =>
  import('@/portals/family/FamilyMorePages').then((m) => ({ default: m.FamilyDocumentsPage })),
)
const FamilyTransportationPage = lazyRoute(() =>
  import('@/portals/family/FamilyMorePages').then((m) => ({ default: m.FamilyTransportationPage })),
)
const StateReportingHome = lazyRoute(() =>
  import('@/features/stateReporting/StateReportingHome').then((m) => ({ default: m.StateReportingHome })),
)
const PrintCenterPage = lazyRoute(() =>
  import('@/features/print/PrintCenterPage').then((m) => ({ default: m.PrintCenterPage })),
)
const AcademicsPage = lazyRoute(() =>
  import('@/features/academics/AcademicsPage').then((m) => ({ default: m.AcademicsPage })),
)
const OperationsRoutes = lazyRoute(() =>
  import('@/features/operations/OperationsHome').then((m) => ({ default: m.OperationsRoutes })),
)
const PreferencesPage = lazyRoute(() =>
  import('@/features/preferences/PreferencesPage').then((m) => ({ default: m.PreferencesPage })),
)
const AccountPage = lazyRoute(() =>
  import('@/features/account/AccountPage').then((m) => ({ default: m.AccountPage })),
)
const WorkspaceHealthPage = lazyRoute(() =>
  import('@/features/reports/WorkspaceHealthPage').then((m) => ({ default: m.WorkspaceHealthPage })),
)
const PlatformHome = lazyRoute(() =>
  import('@/features/platform/PlatformHome').then((m) => ({ default: m.PlatformHome })),
)
const HomePage = lazyRoute(() => import('@/portals/staff/HomePage').then((m) => ({ default: m.HomePage })))
const FamilyDashboard = lazyRoute(() =>
  import('@/portals/family/FamilyDashboard').then((m) => ({ default: m.FamilyDashboard })),
)
const FamilyGradesPage = lazyRoute(() =>
  import('@/portals/family/FamilyPages').then((m) => ({ default: m.FamilyGradesPage })),
)
// Students and guardians get different assignment screens; the route
// component decides which, so neither carries branches for the other.
const FamilyAssignmentsPage = lazyRoute(() => import('@/portals/family/AssignmentsRoute'))
const FamilyAssessmentsPage = lazyRoute(() => import('@/portals/family/FamilyAssessmentsPage').then((m) => ({ default: m.FamilyAssessmentsPage })))
const FamilyAttendancePage = lazyRoute(() =>
  import('@/portals/family/FamilyPages').then((m) => ({ default: m.FamilyAttendancePage })),
)
const StudentListPage = lazyRoute(() =>
  import('@/features/students/StudentListPage').then((m) => ({ default: m.StudentListPage })),
)
const StudentProfilePage = lazyRoute(() =>
  import('@/features/students/StudentProfilePage').then((m) => ({ default: m.StudentProfilePage })),
)
const GradesHome = lazyRoute(() => import('@/features/grades/GradesHome').then((m) => ({ default: m.GradesHome })))
const GradebookV2 = lazyRoute(() => import('@/features/grades/GradebookV2').then((m) => ({ default: m.GradebookV2 })),
)
const AttendanceHome = lazyRoute(() => import('@/features/attendance/AttendanceHome').then((m) => ({ default: m.AttendanceHome })))
const TakeAttendance = lazyRoute(() => import('@/features/attendance/TakeAttendance').then((m) => ({ default: m.TakeAttendance })))
const SectionListPage = lazyRoute(() =>
  import('@/features/academics/SectionListPage').then((m) => ({ default: m.SectionListPage })),
)
const ReportCenterPage = lazyRoute(() =>
  import('@/features/reports/ReportCenterPage').then((m) => ({ default: m.ReportCenterPage })),
)
const SuccessCenterPage = lazyRoute(() =>
  import('@/features/analytics/SuccessCenterPage').then((m) => ({ default: m.SuccessCenterPage })),
)
const MessagesAdminPage = lazyRoute(() => import('@/features/messages/MessagesAdmin').then((m) => ({ default: m.MessagesAdminPage })))
const UsersPage = lazyRoute(() => import('@/features/admin/Users').then((m) => ({ default: m.UsersPage })))
const UserDetailPage = lazyRoute(() => import('@/features/admin/Users').then((m) => ({ default: m.UserDetailPage })))
const SecurityPage = lazyRoute(() => import('@/features/admin/Users').then((m) => ({ default: m.SecurityPage })))
const RolesPage = lazyRoute(() => import('@/features/admin/Users').then((m) => ({ default: m.RolesPage })))
const FamiliesPage = lazyRoute(() => import('@/features/admin/Families').then((m) => ({ default: m.FamiliesPage })))
const FamilyDetailPage = lazyRoute(() => import('@/features/admin/Families').then((m) => ({ default: m.FamilyDetailPage })))
const FamilyContactPage = lazyRoute(() => import('@/features/admin/Families').then((m) => ({ default: m.FamilyContactPage })))
const DocumentsHome = lazyRoute(() => import('@/features/documents/DocumentsPage').then((m) => ({ default: m.DocumentsHome })))
const AuditLogPage = lazyRoute(() => import('@/features/admin/AuditLogPage').then((m) => ({ default: m.AuditLogPage })))

const PrintRoutes = {
  Progress: lazyRoute(() => import('@/features/print/PrintRoutes').then((m) => ({ default: m.ProgressReportPage }))),
  ReportCard: lazyRoute(() => import('@/features/print/PrintRoutes').then((m) => ({ default: m.ReportCardPage }))),
  Transcript: lazyRoute(() => import('@/features/print/PrintRoutes').then((m) => ({ default: m.TranscriptPage }))),
  Roster: lazyRoute(() => import('@/features/print/PrintRoutes').then((m) => ({ default: m.RosterPage }))),
  Batch: lazyRoute(() => import('@/features/print/PrintRoutes').then((m) => ({ default: m.BatchDocumentsPage }))),
  Generic: lazyRoute(() => import('@/features/print/GenericDocumentPage').then((m) => ({ default: m.GenericDocumentPage }))),
  SupportPlan: lazyRoute(() => import('@/features/print/PrintRoutes').then((m) => ({ default: m.SupportPlanPage }))),
  ServiceDelivery: lazyRoute(() => import('@/features/print/PrintRoutes').then((m) => ({ default: m.ServiceDeliveryPage }))),
  PdTranscript: lazyRoute(() => import('@/features/print/PrintRoutes').then((m) => ({ default: m.PdTranscriptPage }))),
}

const LOADING = <Skeleton rows={6} label="Loading" />

export function App() {
  const { session, loading, error, isPlatformConsole } = useSession()
  const portal = currentPortal()
  const location = useLocation()

  // Accepting an invitation happens before there is a session, so this route
  // is matched before the guard below. Sending an invited educator to a
  // sign-in page for an account they do not yet have is where a join flow
  // loses people.
  // The public school calendar needs no account: it shows only events marked Public.
  if (location.pathname.startsWith('/public/calendar/')) {
    return (
      <Suspense fallback={LOADING}>
        <Routes>
          <Route path="/public/calendar/:tenant" element={<PublicCalendarPage />} />
        </Routes>
      </Suspense>
    )
  }

  // A vendor opens an issued purchase order from the emailed link, without an account.
  if (location.pathname.startsWith('/vendor/po/')) {
    return (
      <Suspense fallback={LOADING}>
        <Routes>
          <Route path="/vendor/po/:tenant/:po/:token" element={<VendorPoPage />} />
        </Routes>
      </Suspense>
    )
  }

  if (location.pathname.startsWith('/join/')) {
    return (
      <Suspense fallback={LOADING}>
        <Routes>
          <Route path="/join/:token" element={<JoinPage />} />
        </Routes>
      </Suspense>
    )
  }

  if (loading) {
    return (
      <div className="boot">
        <Card>
          <Skeleton rows={4} label="Signing you in" />
        </Card>
      </div>
    )
  }

  // An invitation or recovery link is used before anyone is signed in.
  if (location.pathname === '/accept-invitation') return <AcceptInvitationPage />

  if (!session) {
    // The error sits above the form rather than replacing it, so a transient
    // network failure does not lock someone out of retrying.
    return (
      <>
        {error && (
          <div className="boot-error" role="alert">
            {error}
          </div>
        )}
        <LoginPage />
      </>
    )
  }

  // An account still using a password it was issued goes no further.
  //
  // The server already refuses every other route while the flag is set, so
  // this is not the control — it is what the user sees instead of an
  // application that would 403 on every click. Placed above the print routes
  // deliberately: a temporary password should not be enough to print a
  // transcript.
  if (session.mustChangePassword) {
    return <ChangePasswordPage />
  }

  // The same for two-step verification the policy requires: the server refuses everything else until it is set up.
  if (session.mfaEnrollmentRequired) {
    return <MfaEnrollPage />
  }

  // Documents bypass the shell entirely.
  if (location.pathname.startsWith('/print/')) {
    return (
      <Suspense fallback={LOADING}>
        <Routes>
          <Route path="/print/support-plan/:studentId" element={<PrintRoutes.SupportPlan />} />
          <Route path="/print/service-delivery/:studentId" element={<PrintRoutes.ServiceDelivery />} />
          <Route path="/print/pd-transcript" element={<PrintRoutes.PdTranscript />} />
          <Route path="/print/progress/:studentId" element={<PrintRoutes.Progress />} />
          <Route path="/print/report-card/:studentId" element={<PrintRoutes.ReportCard />} />
          <Route path="/print/transcript/:studentId" element={<PrintRoutes.Transcript />} />
          <Route path="/print/roster/:sectionId" element={<PrintRoutes.Roster />} />
          <Route path="/print/batch" element={<PrintRoutes.Batch />} />
          <Route path="/print/doc/:kind" element={<PrintRoutes.Generic />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    )
  }

  // The family portal is a parent's or a student's whole application, so
  // that is what they get wherever the page is served: viewing as a parent
  // from the staff site used to draw the staff menu filtered by a parent's
  // permissions, which offered staff pages the parent is refused.
  const familyRole = session.user.role === 'parent_guardian' || session.user.role === 'student'
  const variant = portal === 'family' || familyRole ? 'family' : 'staff'

  return (
    <AppShell variant={variant}>
      <Suspense fallback={LOADING}>
        <Routes>
          {variant === 'staff' ? (
            <>
              {/* One Home for every staff member, shaped by role inside the
                  page itself; an operator's own tenant is not a school at all. */}
              <Route
                path="/"
                element={isPlatformConsole ? <PlatformHome /> : <HomePage />}
              />
              <Route path="/students" element={<StudentListPage />} />
              <Route path="/students/:id" element={<StudentProfilePage />} />
              <Route path="/academics" element={<SectionListPage />} />
              <Route path="/academics/sections" element={<SectionListPage />} />
              <Route path="/academics/*" element={<AcademicsPage />} />
              <Route path="/attendance" element={<AttendanceHome />} />
              <Route path="/attendance/section/:sectionId" element={<TakeAttendance />} />
              <Route path="/attendance/register/:sectionId" element={<TakeAttendance />} />
              {/* The analytics overview for roles that can read gradebooks;
                  it renders the class picker itself for roles that cannot. */}
              <Route path="/grades" element={<GradesHome />} />
              <Route path="/grades/:sectionId" element={<GradebookV2 />} />
              <Route path="/scheduling" element={<SchedulingPage />} />
              {/* A run is a proposal, so it lives beside the schedule rather
                  than inside it — nothing on this page is anybody's
                  timetable until it is committed. */}
              <Route path="/scheduling/runs" element={<ScenarioRunsPage />} />
              <Route path="/courses" element={<CourseCatalogPage />} />
              <Route path="/reports" element={<ReportCenterPage />} />
              <Route path="/reports/workspace-health" element={<WorkspaceHealthPage />} />
              <Route path="/admissions" element={<AdmissionsPage />} />
              <Route path="/forms" element={<FormsPage />} />
              <Route path="/fees" element={<FeesHome />} />
              <Route path="/fees/students/:studentId" element={<FeeStudentPage />} />
              <Route path="/accounts" element={<Navigate to="/fees" replace />} />
              <Route path="/graduation" element={<GraduationPage />} />
              <Route path="/my-class" element={<MyClassPage />} />
              <Route path="/my-class/integrity/:submissionId" element={<IntegrityCheckPage />} />
              <Route path="/counseling" element={<CounselorPage />} />
              <Route path="/assessments" element={<AssessmentsHome />} />
              <Route path="/assessments/sessions/:sessionId" element={<AssessmentSessionPage />} />
              <Route path="/assessments/:assessmentId" element={<AssessmentDetail />} />
              <Route path="/curriculum" element={<CurriculumHome />} />
              <Route path="/curriculum/maps/:mapId" element={<CurriculumMapDetail />} />
              <Route path="/transportation/*" element={<TransportationPage />} />
              <Route path="/dismissal" element={<DismissalPage />} />
              <Route path="/health-office" element={<HealthOfficePage />} />
              <Route path="/rounds" element={<NurseRoundsPage />} />
              <Route path="/discipline" element={<DisciplinePage />} />
              <Route path="/activities" element={<ActivitiesHome />} />
              <Route path="/activities/:id" element={<ActivityWorkspace />} />
              <Route path="/cafeteria" element={<CafeteriaPage />} />
              <Route path="/cafeteria/pos" element={<PosPage />} />
              <Route path="/assets/*" element={<AssetsRoutes />} />
              <Route path="/staff" element={<StaffHome />} />
              <Route path="/staff/:id" element={<StaffRecordPage />} />
              <Route path="/absences" element={<AbsencesHome />} />
              <Route path="/absences/r/:id" element={<AbsenceDetailPage />} />

              <Route path="/sub-today" element={<SubAssignmentPage />} />
              <Route path="/evaluations" element={<EvaluationsHome />} />
              <Route path="/evaluations/c/:id" element={<CycleDetailPage />} />
              <Route path="/evaluations/walkthrough" element={<WalkthroughPage />} />
              <Route path="/evaluations/classic" element={<Navigate to="/evaluations" replace />} />
              <Route path="/emergency" element={<EmergencyPage />} />
              <Route path="/visitors" element={<VisitorsPage />} />
              <Route path="/library/*" element={<LibraryPage />} />
              <Route path="/facilities/*" element={<FacilitiesPage />} />
              <Route path="/finance" element={<FinancePage />} />
              <Route path="/purchasing" element={<PurchasingHome />} />
              <Route path="/purchasing/requests/:id" element={<PurchaseRequestPage />} />
              <Route path="/purchasing/orders/:id" element={<PurchaseOrderPage />} />
              <Route path="/state-reporting/*" element={<StateReportingHome />} />
              <Route path="/print-center" element={<PrintCenterPage />} />
              <Route path="/documents/*" element={<DocumentsHome />} />
              <Route path="/students/success" element={<SuccessCenterPage />} />
              <Route path="/administration" element={<AdministrationPage />} />
              <Route path="/administration/audit" element={<AuditLogPage />} />
              <Route path="/administration/messages" element={<MessagesAdminPage />} />
              <Route path="/administration/users" element={<UsersPage />} />
              <Route path="/administration/users/:id" element={<UserDetailPage />} />
              <Route path="/administration/security" element={<SecurityPage />} />
              <Route path="/administration/families" element={<FamiliesPage />} />
              <Route path="/administration/families/contacts/:id" element={<FamilyContactPage />} />
              <Route path="/administration/families/:id" element={<FamilyDetailPage />} />
              <Route path="/administration/roles" element={<RolesPage />} />
              {/* Communications was folded into Messages; old links land on its Announcements. */}
              <Route path="/communications/*" element={<Navigate to="/messages?view=announcements" replace />} />
              <Route path="/messages/*" element={<MessagesHome />} />
              <Route path="/operations/*" element={<OperationsRoutes />} />
              <Route path="/administration/*" element={<Navigate to="/administration" replace />} />
              <Route path="/work" element={<MyWorkPage />} />
              <Route path="/calendar" element={<CalendarHome />} />
              <Route path="/special-programs" element={<SpecialProgramsPage />} />
              <Route path="/collect" element={<CollectPage />} />
              <Route path="/growth" element={<GrowthPage />} />
              {/* /queues used to be a coming-soon stub; My Work is that page, built. */}
              <Route path="/queues" element={<MyWorkPage />} />

              {/* The programme. Tester-facing and staff-facing surfaces are
                  separate routes because they are separate jobs, and the API
                  refuses whichever one the account is not entitled to. */}
              <Route path="/feedback" element={<FeedbackLabPage />} />
              <Route path="/feedback/mine" element={<FeedbackLabPage />} />
              <Route path="/administration/feedback" element={<FeedbackInboxPage />} />
              <Route path="/administration/focus-group" element={<FocusGroupPage />} />
              <Route path="/administration/focus-group/:id" element={<TesterDetailPage />} />
            </>
          ) : (
            <>
              <Route path="/" element={<FamilyDashboard />} />
              <Route path="/classes" element={<FamilyGradesPage />} />
              <Route path="/grades" element={<FamilyGradesPage />} />
              <Route path="/assignments" element={<FamilyAssignmentsPage />} />
              <Route path="/attendance" element={<FamilyAttendancePage />} />
              <Route path="/assessments" element={<FamilyAssessmentsPage />} />
              <Route path="/apply" element={<ApplyPage />} />
              <Route path="/forms" element={<FamilyFormsPage />} />
              <Route path="/fees" element={<FamilyFeesPage />} />
              <Route path="/student/:id" element={<StudentPage />} />
              <Route path="/course-requests" element={<CourseRequestsPage />} />
              <Route path="/graduation" element={<FamilyGraduationPage />} />
              <Route path="/college-career" element={<CollegeCareerPage />} />
              <Route path="/health" element={<FamilyHealthPage />} />
              <Route path="/activities" element={<FamilyActivitiesPage />} />
              <Route path="/cafeteria" element={<FamilyCafeteriaPage />} />
              <Route path="/library" element={<FamilyLibraryPage />} />
              <Route path="/dismissal" element={<FamilyDismissalPage />} />
              <Route path="/schedule" element={<FamilySchedulePage />} />
              <Route path="/calendar" element={<FamilyCalendarPage />} />
              <Route path="/documents" element={<FamilyDocumentsPage />} />
              <Route path="/transportation" element={<FamilyTransportationPage />} />
              <Route path="/messages" element={<FamilyMessagesPage />} />
                          </>
          )}
          <Route path="/preferences" element={<PreferencesPage variant={variant} />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AppShell>
  )
}

/**
 * An honest placeholder.
 *
 * Names the area and gives a way back. Deliberately not dressed up to look
 * finished — a convincing empty screen is how a gap goes unnoticed until
 * someone needs the feature.
 */

function NotFound() {
  return (
    <Card>
      <ErrorState message="That page does not exist." />
      <p style={{ textAlign: 'center', paddingBottom: 'var(--space-6)' }}>
        <Link to="/">Back to the dashboard</Link>
      </p>
    </Card>
  )
}
